<template>
  <div>
    <hb-form label="Occupancy Tiers" required full>
      <OccupancyThresholdSetting
        v-for="(threshold, key) in thresholds"
        :key="key"
        v-model="thresholds[key]"
        :promotions="activePromotions"
        :index="key"
        @remove="removeThreshold"
        ref="occupancyThresholdSetting"
      />
      <hb-link @click="addThreshold">+ Add Threshold</hb-link>
    </hb-form>
  </div>
</template>
<script>
import OccupancyThresholdSetting from "./OccupancyThresholdSetting.vue";
import { cloneDeep } from "lodash";

import { mapGetters } from "vuex";

export default {
  name: "OccupancyBasedPromotion",
  components: {
    OccupancyThresholdSetting,
  },
  data() {
    return {
      defaultThreshold: {
        min: 0,
        promotion_id: "",
      },
      thresholds: [
        {
          min: 0,
          promotion_id: "",
        },
      ],
      occupancyPercentageList: [],
    };
  },
  props: {
    stepData: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters({
      getActivePromotions: "revManStore/getActivePromotions",
    }),
    activePromotions() {
      return this.getActivePromotions.filter(
        (promo) =>
          promo?.label === "promotion" &&
          !promo?.conditions?.includes("existing_tenants")
      );
    },
  },
  methods: {
    addThreshold() {
      this.thresholds.push(cloneDeep(this.defaultThreshold));
    },
    removeThreshold(index) {
      this.thresholds.splice(index, 1);
    },
    async validate() {
      this.occupancyPercentageList = [];
      let errorMessages = [];

      const promises = this.$refs.occupancyThresholdSetting.map(
        async (setting) => {
          let threshold = setting.threshold;

          //Occupancy validation
          let occupancyError = this.hasOccupancyError(threshold.min);

          if (occupancyError)
            errorMessages.push(
              "Occupancy field is required with a value ranging between 0-99 and not repeated. Decimal values are not allowed"
            );

          if (!threshold.promotion_id)
            errorMessages.push("Promotion field is required.");

          //set corresponding field error to highlight the field with red color.
          setting.showError({
            min: occupancyError,
            promotion_id: !threshold.promotion_id,
          });

          //Triggers v-validate in fields to highlight fields with errors
          await setting.$validator.validateAll();
        }
      );

      errorMessages = [...new Set(errorMessages)];

      //Display error message for each field type
      errorMessages.forEach((msg) => {
        this.$validator.errors.add({
          field: "min",
          msg,
        });
      });

      await Promise.all(promises);
    },
    /**
     * Function to check for errors in occupancy by field
     * Conditions:
     *  - Value is required
     *  - Value must be a natural number from 0 to 99 and up to 2 decimal places.
     *  - Value shouldn't be repeated
     *
     * @param {String} value occupancy field value entered by user
     *
     * @returns {Boolean} true or false based on above conditions
     */
    hasOccupancyError(value) {
      if (value || value === 0) {
        //Value must be a natural number less than 100
        if (/^(0|[1-9][0-9]?)(\.[0-9]{1,2})?$/.test(value)) {
          let occupancy = parseInt(value);
          //Occupancy cannot be repeated
          if (!this.occupancyPercentageList.includes(occupancy)) {
            this.occupancyPercentageList.push(occupancy);
            return false;
          }
        }
      }
      return true;
    },
    async submit() {
      this.$validator.errors.clear();
      await this.validate();
      let data = {
        status: !this.errors.items.length,
        validatedData: {
          plan_default: this.thresholds,
        },
      };
      return data;
    },
    updateNoPromotion(value, type = "remove") {
      return value.map((item) => {
        if (type == "remove")
          if (item.promotion_id == "no-promotion") item.promotion_id = null;
          else if (item.promotion_id == null)
            item.promotion_id = "no-promotion";
        return item;
      });
    },
    async resetData() {
      this.$validator?.errors?.clear();
      const promises = this.$refs?.occupancyThresholdSetting?.map(
        async (setting) => {
          //clear all error fields and validations.
          setting?.showError({
            min: false,
            promotion_id: false,
          });
        }
      );
      await Promise.all(promises);
      this.thresholds = [
        {
          min: null,
          promotion_id: null,
        },
      ];
    },
  },
  watch: {
    stepData: {
      handler(value) {
        this.thresholds = value.plan_default ?? this.thresholds;
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
